import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { listPosts } from "../../../apis/services/pgApi";
import { Calendar, Clock, ArrowRight, ArrowLeft, User } from "lucide-react";
import BlockRenderer, { extractToc } from "../lib/blockRenderer";

// ─── BLOG DETAIL ──────────────────────────────────────────────────────────────
// Shared active-heading tracking for both the desktop sticky sidebar and the
// mobile horizontal chip bar, so scrolling highlights the same section in
// whichever one is currently visible.
function useTocScrollSpy(toc) {
  const [active, setActive] = useState(toc[0]?.id || null);
  const clickLockRef = useRef(0);

  useEffect(() => {
    if (!toc.length) return undefined;
    // Highlight the section whose heading has most recently crossed the
    // reading line near the top of the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        // Ignore observer callbacks fired right after a smooth-scroll click
        // so the clicked item stays active while the scroll animates.
        if (Date.now() - clickLockRef.current < 700) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-450px 0px -70% 0px", threshold: 0 }
    );
    toc.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [toc]);

  const onClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 88;
    clickLockRef.current = Date.now();
    setActive(id);
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return [active, onClick];
}

function StickyToc({ toc, active, onClick }) {
  if (!toc.length) return null;
  return (
    <aside className="bl-toc" aria-label="Table of contents">
      <div className="bl-toc__label">On this page</div>
      <ul className="bl-toc__list">
        {toc.map((item) => (
          <li
            key={item.id}
            className={
              `bl-toc__item bl-toc__item--l${item.level}` +
              (active === item.id ? " is-active" : "")
            }
          >
            <a href={`#${item.id}`} onClick={(e) => onClick(e, item.id)}>
              {item.content}
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
}

// Mobile/tablet stand-in for the sidebar TOC: a sticky bar of horizontally
// scrollable chips, shown only below the desktop breakpoint (see
// .bl-toc-mobile in _blog.scss).
function MobileToc({ toc, active, onClick }) {
  if (!toc.length) return null;
  return (
    <nav className="bl-toc-mobile" aria-label="Table of contents">
      <div className="bl-toc-mobile__scroll">
        <span className="bl-toc-mobile__label">On this page</span>
        {toc.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className={"bl-toc-mobile__chip" + (active === item.id ? " is-active" : "")}
            onClick={(e) => onClick(e, item.id)}
          >
            {item.content}
          </a>
        ))}
      </div>
    </nav>
  );
}

function AuthorBox({ post }) {
  const author = (post.author || "").trim();
  if (!author && !post.authorBio && !post.authorImage) return null;
  const initial = (author || "?").charAt(0).toUpperCase();
  return (
    <div className="bl-author-box">
      <div className="bl-author-box__avatar" aria-hidden="true">
        {post.authorImage
          ? <img src={post.authorImage} alt={author} />
          : <span>{initial}</span>}
      </div>
      <div className="bl-author-box__body">
        <div className="bl-author-box__label">About the author</div>
        <div className="bl-author-box__name">{author || "Anonymous"}</div>
        {post.authorBio && <p className="bl-author-box__bio">{post.authorBio}</p>}
      </div>
    </div>
  );
}

function BlogDetail({ post, onBack }) {
  const toc = useMemo(() => extractToc(post.blocks || []), [post.blocks]);
  const [active, onTocClick] = useTocScrollSpy(toc);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [post.slug]);

  return (
    <div>
      <section className="bl-section bl-detail-top">
        <div className="bl-container">
          <button onClick={onBack} className="bl-back-btn">
            <ArrowLeft className="bl-back-icon" /> Back to Blog
          </button>

          {/* Mobile/tablet only — the sidebar TOC below is hidden under 1024px */}
          <MobileToc toc={toc} active={active} onClick={onTocClick} />

          <div className="bl-detail-layout">
            <div className="bl-detail-layout__aside">
              <StickyToc toc={toc} active={active} onClick={onTocClick} />
            </div>

            <div className="bl-detail-layout__body">
              <span className="bl-badge bl-detail-badge">{post.category}</span>
              <h1 className="bl-fluid-4xl bl-fw-800 bl-detail-title">{post.title}</h1>
              <div className="bl-post-meta">
                <span><User className="bl-meta-icon" /> {post.author}</span>
                <span><Calendar className="bl-meta-icon" /> {post.date}</span>
                <span><Clock className="bl-meta-icon" /> {post.readTime}</span>
              </div>

              <article className="bl-article-body">
                <BlockRenderer blocks={post.blocks || []} />
                <AuthorBox post={post} />
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="bl-section">
        <div className="bl-container">
          <div className="bl-cta-box">
            <h2 className="bl-fluid-2xl bl-fw-800 bl-cta-title">Ready to go broker-free?</h2>
            <p className="bl-cta-sub">Join the AapkaPG Partner Network and start filling beds directly.</p>
            <a href="/listings" className="bl-btn-primary bl-cta-btn">Get Started</a>
          </div>
        </div>
      </section>
    </div>
  );
}


// ─── BLOG LIST ────────────────────────────────────────────────────────────────
// Posts come from the API (see src/lib/blogApi.js) — no static content, no
// localStorage. Drafts (published === false) are hidden from the public list.
function BlogList({ posts }) {
  const navigate = useNavigate();

  const visible = (posts || []).filter((p) => p.published !== false);
  const featured = visible.find((p) => p.featured);
  const rest = visible.filter((p) => p !== featured);

  return (
    <div className="bl-root">

      {/* Hero */}
      <section className="bl-hero">
        <div className="bl-hero__bg" />
        <div className="bl-hero__glow" />
        <div className="bl-container bl-hero__content">
          <span className="bl-badge"><span className="bl-badge__dot" /> Blog</span>
          <h1 className="bl-fluid-hero bl-hero__title">
            Insights for <span className="bl-primary">PG Owners</span> &amp; Renters
          </h1>
          <p className="bl-fluid-lg bl-hero__sub">
            Tips, strategies, and stories from the frontlines of India's PG revolution.
          </p>
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="bl-section bl-section--featured">
          <div className="bl-container">
            <div className="bl-featured" onClick={() => navigate(`/blog/${featured.slug}`)}>
              <div className="bl-featured__left">
                <span className="bl-featured__badge">Featured</span>
                <h2 className="bl-fluid-2xl bl-fw-800 bl-featured__title">{featured.title}</h2>
                <p className="bl-featured__excerpt">{featured.excerpt}</p>
                <div className="bl-featured__meta">
                  <span><Calendar className="bl-meta-icon" /> {featured.date}</span>
                  <span><Clock className="bl-meta-icon" /> {featured.readTime}</span>
                </div>
              </div>
              <div className="bl-featured__right">
                <span className="bl-badge">{featured.category}</span>
                <p className="bl-muted bl-featured__right-excerpt">{featured.excerpt}</p>
                <span className="bl-read-more">
                  Read full article <ArrowRight className="bl-read-more-icon" />
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All articles */}
      <section className="bl-section bl-section--muted">
        <div className="bl-container">
          <h2 className="bl-fluid-2xl bl-fw-800 bl-articles-heading">All Articles</h2>
          {visible.length === 0 && (
            <p className="bl-muted">No articles published yet — check back soon.</p>
          )}
          <div className="bl-posts-grid">
            {rest.map((post) => (
              <div key={post.slug} className="bl-post-card" onClick={() => navigate(`/blog/${post.slug}`)}>
                <div className="bl-post-card__strip" />
                <div className="bl-post-card__body">
                  <span className="bl-badge bl-post-card__badge">{post.category}</span>
                  <h3 className="bl-fw-700 bl-post-card__title">{post.title}</h3>
                  <p className="bl-muted bl-post-card__excerpt">{post.excerpt}</p>
                  <div className="bl-post-card__footer">
                    <span><User className="bl-meta-icon" /> {post.author}</span>
                    <span><Clock className="bl-meta-icon" /> {post.readTime}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}


// ─── PAGE ROOT ────────────────────────────────────────────────────────────────
// Handles both /blog (list) and /blog/:slug (detail). Posts are fetched from
// the API on mount — see src/lib/blogApi.js.
export default function Blog() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listPosts()
      .then((data) => { if (!cancelled) setPosts(data); })
      .catch((err) => { if (!cancelled) setError(err.message || "Couldn't load the blog."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bl-root" style={{ marginTop: "5vh" }}>
        <section className="bl-section">
          <div className="bl-container"><p className="bl-muted">Loading articles…</p></div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bl-root">
        <section className="bl-section">
          <div className="bl-container">
            <h1 className="bl-fluid-2xl bl-fw-800">Couldn't load the blog</h1>
            <p className="bl-muted" role="alert">{error}</p>
          </div>
        </section>
      </div>
    );
  }

  if (slug) {
    const post = posts.find((p) => p.slug === slug);
    if (!post) {
      return (
        <div className="bl-root">
          <section className="bl-section">
            <div className="bl-container">
              <h1 className="bl-fluid-2xl bl-fw-800">Article not found</h1>
              <p className="bl-muted">This post may have been unpublished or moved.</p>
              <button className="bl-back-btn" onClick={() => navigate("/blog")}>Back to Blog</button>
            </div>
          </section>
        </div>
      );
    }
    return (
      <div className="bl-root">
        <BlogDetail post={post} onBack={() => navigate("/blog")} />
      </div>
    );
  }

  return <BlogList posts={posts} />;
}